import type { AxiosResponse } from 'axios';
import axios from 'axios';

import type { PharmacistOrderTableView } from '@/context/orders/PharmacistOrders';
import type {
  AddMultipleStockToInventoryPayload,
  GenericStatusResponse,
  OrderStatus,
  Pharmacist,
  PharmacistOrder,
  PharmacistReasonStatus,
  ProcessDispenseResult,
  Product,
  TableSortOrder,
  UnableToDispenseInfo,
  UnableToDispenseProduct,
} from '@montugroup/circuit-shared/types/index';

import type { apiV1Contract } from '@montugroup/circuit-api-contracts';

import type { ClientInferResponseBody } from '@ts-rest/core';
import { DateTime } from 'luxon';
import settings from '../constants/constants';

/**
 * Use `getPharmacyProducts` from pharmacy.service
 * @deprecated
 */
const getPharmacyProducts = () => axios.get(`${settings.url}/pharmacist/pharmacyProducts`);

const getPharmacistDetails = async () => axios.get<Pharmacist>(`${settings.url}/pharmacist/details`);

/**
 * Use `getInventoryHistory` from pharmacy.service
 *  @deprecated
 */
const getInventoryHistory = (params: unknown) =>
  axios.get(`${settings.url}/pharmacist/getInventoryHistory`, { params });

const addMultipleStockToInventory = async (data: AddMultipleStockToInventoryPayload) =>
  axios.post<GenericStatusResponse>(`${settings.url}/pharmacist/addMultipleStockToInventory`, data);

const processDispensing = async (orderIds: number[]) =>
  axios.post<ProcessDispenseResult>(`${settings.url}/pharmacist/process-dispensing`, { orderIds });

const getReasonStatusList = () =>
  axios.get<PharmacistReasonStatus[]>(`${settings.url}/pharmacist/unableToDispenseReasons`);

const unableToDispense = (data: UnableToDispenseProduct[]) =>
  axios.post(`${settings.url}/pharmacist/saveUnableToDispense`, data);

export type UnableToDispenseResponse = AxiosResponse & {
  orderProductData: UnableToDispenseInfo;
};

const getUnableToDispenseInfo = async (orderId: number) => {
  try {
    const response = await axios.get<UnableToDispenseResponse>(
      `${settings.url}/order/getUnableToDispenseOrderDetails/${orderId}`,
    );
    return response?.data?.orderProductData;
  } catch {
    throw new Error('Failed to fetch order details');
  }
};

export type GetCountDataResponse = number;

const getCountData = () => axios.get<GetCountDataResponse>(`${settings.url}/pharmacist/process-dispensing/count`);

const confirmDispenseOrder = (orderIds?: number[]) => {
  if (orderIds) {
    return axios.post<ClientInferResponseBody<typeof apiV1Contract.dispensing.confirmDispensing, 200>>(
      `${settings.url}/pharmacist/process-dispensing/confirm`,
      {
        orderIds,
      },
    );
  }
  return axios.post<ClientInferResponseBody<typeof apiV1Contract.dispensing.confirmDispensing, 200>>(
    `${settings.url}/pharmacist/process-dispensing/confirm`,
  );
};

export type ProductDetails = Pick<Product, 'name' | 'id'>;

export type PharmacistOrdersResponse = {
  count: number;
  orders: PharmacistOrder[];
};

const getPharmacistOrders = (
  pageSize: number,
  page: number,
  sortingOrder: TableSortOrder,
  filter: string,
  activeTab: PharmacistOrderTableView,
  statusFilter: OrderStatus[],
  startDate: Date | null,
  endDate: Date | null,
  signal?: AbortSignal,
) =>
  axios.get<PharmacistOrdersResponse>(`${settings.url}/pharmacist/orders`, {
    signal,
    params: {
      limit: pageSize,
      offset: page * pageSize,
      sortingOrder: sortingOrder.name,
      sortOrder: sortingOrder.reverse ? 'DESC' : 'ASC',
      filter,
      statusFilter: statusFilter.map((status) => status.id),
      ...(startDate && { startDate: DateTime.fromJSDate(startDate).toJSON() }),
      ...(endDate && { endDate: DateTime.fromJSDate(endDate).endOf('day').toJSON() }),
      ts: new Date().getTime(),
      activeTab,
    },
  });

export interface PharmacistBatchOrder {
  batchNumber: number;
  dispensedTimestamp: string;
  orderCount: number;
}

const getPharmacistBatchDispenseOrders = async (
  pageSize: number,
  page: number,
): Promise<AxiosResponse<PharmacistBatchOrder[]>> => {
  const { data: pharmacistDetails } = await getPharmacistDetails();
  const pharmacyId = pharmacistDetails.pharmacy_id;
  const response = await axios.get(`${settings.adminApiUrl}/pharmacy/get-dispense-summary/${pharmacyId}`, {
    params: {
      limit: pageSize,
      offset: page * pageSize,
    },
  });

  return response.data;
};

const getPharmacistDispenseDocument = async (dispenseId: number): Promise<AxiosResponse<{ signedUrl: string }>> => {
  const response = await axios.get(`${settings.adminApiUrl}/pharmacy/get-dispensed-order-doc/${dispenseId}`, {});

  return response.data;
};

export {
  addMultipleStockToInventory,
  confirmDispenseOrder,
  getCountData,
  getInventoryHistory,
  getPharmacistBatchDispenseOrders,
  getPharmacistDetails,
  getPharmacistDispenseDocument,
  getPharmacistOrders,
  getPharmacyProducts,
  getReasonStatusList,
  getUnableToDispenseInfo,
  processDispensing,
  unableToDispense,
};
